import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Mail, CheckCircle, RefreshCw, ArrowLeft,
  AlertCircle, Clock, Send
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { freemiumService } from '@/services/freemium.service'

export function EmailVerification() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [isResending, setIsResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending')

  // Get email from location state or localStorage
  const email = location.state?.email || localStorage.getItem('verification_email')
  const message = location.state?.message || 'Please check your email to verify your account.'

  useEffect(() => {
    // Check if user just signed up successfully
    const signupSuccess = localStorage.getItem('freemium_signup_success')
    if (signupSuccess) {
      localStorage.removeItem('freemium_signup_success')
    }

    // If no email available, redirect to signup
    if (!email) {
      navigate('/freemium-signup')
      return
    }

    // Store email for future use
    localStorage.setItem('verification_email', email)

    // Check for verification token in URL (if coming from email link)
    const urlParams = new URLSearchParams(location.search)
    const token = urlParams.get('token')

    if (token) {
      verifyEmail(token)
    }
  }, [email, location.search, navigate])

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [resendCooldown])

  const verifyEmail = async (token: string) => {
    try {
      await freemiumService.verifyEmail(token)
      setVerificationStatus('success')
      localStorage.removeItem('verification_email')

      toast({
        title: 'Email Verified!',
        description: 'Your account has been successfully verified. You can now sign in.',
      })

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/auth/login', {
          state: {
            email,
            message: 'Account verified! Please sign in to continue.'
          }
        })
      }, 2000)
    } catch (error: any) {
      setVerificationStatus('error')
      toast({
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Please try requesting a new verification email.',
        variant: 'destructive'
      })
    }
  }

  const handleResendEmail = async () => {
    if (!email || resendCooldown > 0) return

    setIsResending(true)
    try {
      await freemiumService.resendVerificationEmail(email)
      setResendCooldown(60) // 60 second cooldown

      toast({
        title: 'Email Sent',
        description: 'A new verification email has been sent to your inbox.',
      })
    } catch (error: any) {
      toast({
        title: 'Failed to Send Email',
        description: error.response?.data?.detail || 'Please try again later.',
        variant: 'destructive'
      })
    } finally {
      setIsResending(false)
    }
  }

  const handleGoBack = () => {
    navigate('/freemium-signup')
  }

  const handleGoToLogin = () => {
    navigate('/auth/login', { state: { email } })
  }

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card className="text-center shadow-xl border-0">
            <CardHeader className="space-y-4 pb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center"
              >
                <CheckCircle className="w-8 h-8 text-green-600" />
              </motion.div>
              <CardTitle className="text-2xl text-green-800">Email Verified!</CardTitle>
              <CardDescription className="text-green-600">
                Your account has been successfully verified. Redirecting you to sign in...
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleGoToLogin} className="w-full">
                Continue to Sign In
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center space-y-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center"
            >
              <Mail className="w-8 h-8 text-primary-600" />
            </motion.div>
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription>
              {message}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {email && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  We sent a verification link to <strong>{email}</strong>
                </AlertDescription>
              </Alert>
            )}

            {verificationStatus === 'error' && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Verification failed. Please try requesting a new email or contact support.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="text-sm text-gray-600 space-y-2">
                <p>📧 Check your inbox and spam folder</p>
                <p>🔗 Click the verification link in the email</p>
                <p>⏱️ The link expires in 24 hours</p>
              </div>

              <Button
                onClick={handleResendEmail}
                disabled={isResending || resendCooldown > 0}
                variant="outline"
                className="w-full"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  <>
                    <Clock className="mr-2 h-4 w-4" />
                    Resend in {resendCooldown}s
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Resend Email
                  </>
                )}
              </Button>

              <div className="flex gap-2">
                <Button
                  onClick={handleGoBack}
                  variant="ghost"
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Signup
                </Button>
                <Button
                  onClick={handleGoToLogin}
                  variant="ghost"
                  className="flex-1"
                >
                  Already verified? Sign In
                </Button>
              </div>
            </div>

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <a href="/support" className="text-primary-600 hover:underline">
                  Contact Support
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
